import { EventEmitter, Injectable } from "@angular/core";
import { DateTime } from "luxon";
import { isNullOrEmpty } from "../../gyzmo-commons/helpers/null.helper";
import { DateProvider } from "../../gyzmo-commons/interfaces/dateProvider";
import { AppSqlProvider } from "../../gyzmo-commons/persistence/app.sql.provider";
import { DeliveryDbDao } from "../dao/db/delivery.db.dao";
import { DeliveryWsDao } from "../dao/ws/delivery.ws.dao";
import { ExpenseWsDao } from "../dao/ws/expense.ws.dao";
import { TimeWsDao } from "../dao/ws/timeWsDao";
import { DeliveryDto } from "../dto/delivery.dto";
import { DeliveryLinkedMovementDto } from "../dto/deliveryLinkedMovement.dto";
import { ExpenseDto } from "../dto/expense.dto";
import { ExpenseCategoryDto } from "../dto/expenseCategory.dto";
import { ExpenseStateDto } from "../dto/expenseState.dto";
import { TimeDto } from "../dto/time.dto";
import { UserDto } from "../dto/user.dto";
import { DeliveryStates } from "../interfaces/deliveryStates";
import { LinkedMovementDtoGroup } from "../interfaces/linked-movement-dto.group";
import { Delivery } from "../models/delivery.model";
import { ServersConnectionsProvider } from "../providers/serversConnections.provider";

@Injectable({
    providedIn: "root",
})
export class DeliveryService {
    private synchronisationFinishedSubject = new EventEmitter<{ day: DateTime, orders: DeliveryDto[] }>();

    constructor(private expenseWsDao: ExpenseWsDao,
                private dateProvider: DateProvider,
                private timeWsDao: TimeWsDao,
                private deliveryWsDao: DeliveryWsDao,
                private deliveryDbDao: DeliveryDbDao,
                private serversConnectionsProvider: ServersConnectionsProvider,
                private appSqlProvider: AppSqlProvider) {
    }

    private static isDeliverySame(localDelivery: Delivery, remoteDelivery: DeliveryDto) {
        return localDelivery.movement.id == remoteDelivery.movement.id;
        /*
         TODO : Dans l'idéal il faudrait un test comme cela, car pour l'instant cela ne synchronise
         pas le changement de conducteur ni la réouverture d'un mouvement clos
         mais l'object n'est pas hydraté
         return localDelivery.movement.id == remoteDelivery.movement.id
         && localDelivery.movement.driver.id == remoteDelivery.movement.driver.id
         && (localDelivery.movement.isClosed == 1) == remoteDelivery.movement.isClosed;
         */
    }

    public getSynchronisationFinishedObservable(): EventEmitter<{ day: DateTime, orders: DeliveryDto[] }> {
        return this.synchronisationFinishedSubject;
    }

    public publishSynchronisationFinished(data: { day: DateTime, orders: DeliveryDto[] }) {
        this.synchronisationFinishedSubject.emit(data);
    }

    public getLinkedMovements(deliveryDto: DeliveryDto, hydrate = false): Promise<DeliveryLinkedMovementDto[]> {
        return this.deliveryWsDao.getLinkedMovements(this.serversConnectionsProvider.getPrimaryServerConnection(), deliveryDto.movement.id, hydrate);
    }

    public getCountBetweenDates(startDate: DateTime, endDate: DateTime, myThirdPartyId: string): Promise<number[][]> {
        return new Promise<number[][]>(async (resolve) => {
            let allDeliveries = await this.deliveryWsDao.getList(this.serversConnectionsProvider.getPrimaryServerConnection(), startDate, endDate)
                .catch(reason => {
                    return this.deliveryDbDao.getList(startDate, endDate, false, false)
                        .then((deliveries: Delivery[]) => {
                            let result = [];
                            deliveries.forEach(delivery => {
                                result.push(DeliveryDto.fromModel(delivery));
                            });
                            return result;
                        });
                });

            let result: number[][] = [];
            let activeDay: DateTime = startDate;
            while (activeDay <= endDate) {
                let counts: number[] = [
                    allDeliveries.filter((deliveryDto: DeliveryDto) => {
                        let passFilter = deliveryDto.isOnDay(activeDay) && deliveryDto.driver.id == myThirdPartyId;
                        return passFilter;
                    }).length,
                    allDeliveries.filter((deliveryDto: DeliveryDto) => {
                        return false;
                    }).length,
                    allDeliveries.filter((deliveryDto: DeliveryDto) => {
                        let passFilter = deliveryDto.isOnDay(activeDay) && deliveryDto.driver.id != myThirdPartyId;
                        return passFilter;
                    }).length,
                ];

                result.push(counts);

                activeDay = activeDay.plus({ days: 1 });
            }

            resolve(result);
        });
    }

    public async getListByDate(date: DateTime, deliveryPickerIdFilter?: string): Promise<DeliveryDto[]> {
        let results = await this.synchronize(date, deliveryPickerIdFilter)
            .catch(reason => {
                return this.getOfflineListByDate(date, true, true, deliveryPickerIdFilter);
            });

        return results;
    }

    public getByIdOffline(id: string): Promise<DeliveryDto> {
        return this.deliveryDbDao.get(id, true)
            .then(async delivery => {
                return DeliveryDto.fromModel(delivery);
            });
    }

    public async updateMovement(delivery: DeliveryDto) {
        await this.deliveryWsDao.save(this.serversConnectionsProvider.getPrimaryServerConnection(), delivery);

        return delivery;
    }

    public async changeState(delivery: DeliveryDto, deliveryState: DeliveryStates) {
        delivery.state = deliveryState;
        await this.deliveryDbDao.save(delivery.toModel());

        return delivery;
    }

    public async recordLunch(user: UserDto, delivery: DeliveryDto, selectedLunchId: string) {
        let expense = new ExpenseDto();
        delete expense.id;
        delete expense.supplier;

        expense.kilometers = 1;
        expense.driver = delivery.driver;

        expense.date = delivery.startDate;
        expense.amount = 0;

        expense.type = "R";
        expense.linkedMovement = { id: delivery.movement.id };

        expense.state = new ExpenseStateDto();
        expense.state.id = "REPAS_PREPA";

        expense.category = new ExpenseCategoryDto();
        expense.category.id = selectedLunchId;

        expense.startDate = this.dateProvider.now();
        expense.endDate = this.dateProvider.now();

        expense["paymentMethod"] = { id: "SANS" };

        await this.expenseWsDao.save(this.serversConnectionsProvider.getPrimaryServerConnection(), expense);
    }

    public recordTimeForGroup(group: LinkedMovementDtoGroup, time: TimeDto) {
        return this.timeWsDao.recordTimeForGroup(this.serversConnectionsProvider.getPrimaryServerConnection(), group, time);
    }

    public recordTimeForDelivery(deliveryDto: DeliveryDto, time: TimeDto) {
        return this.timeWsDao.recordTimeForDelivery(this.serversConnectionsProvider.getPrimaryServerConnection(), deliveryDto, time);
    }

    private getOfflineListByDate(date: DateTime,
                                 hydrateMovement: boolean,
                                 hydrateObjects: boolean,
                                 driverIdFilter?: string): Promise<DeliveryDto[]> {
        const startDate = date.startOf("day");
        const endDate = date.endOf("day");

        return this.deliveryDbDao.getList(startDate, endDate, hydrateMovement, hydrateObjects)
            .then(deliveries => {
                let result = [];

                deliveries.forEach(delivery => {
                    if (!isNullOrEmpty(driverIdFilter)) {
                        if (delivery.driver.id == driverIdFilter) {
                            result.push(DeliveryDto.fromModel(delivery));
                        }
                    } else {
                        result.push(DeliveryDto.fromModel(delivery));
                    }
                });

                return result;
            });
    }

    private synchronize(date: DateTime, deliveryPickerIdFilter?: string): Promise<DeliveryDto[]> {
        return new Promise<DeliveryDto[]>((resolve, reject) => {
            let promises = [];
            let remoteDeliveries: DeliveryDto[] = [];
            let localDeliveries: Delivery[] = [];

            const startDate = date.startOf("day");
            const endDate = date.endOf("day");

            this.appSqlProvider.enableBulkWriting();

            promises.push(this.deliveryWsDao.getList(this.serversConnectionsProvider.getServerConnection(), startDate, endDate, false)
                .then(value => {
                    remoteDeliveries = remoteDeliveries.concat(value);
                }));
            promises.push(this.deliveryDbDao.getList(startDate, endDate, false, false)
                .then(value => {
                    localDeliveries = value;
                }));

            Promise.all(promises)
                .then(ignored => {
                    let synchronizationNeeded = false;
                    let synchronisationPromises = [];

                    remoteDeliveries.forEach(remoteLtc => {
                        let localLtc = localDeliveries.find(localLtc => {
                            return DeliveryService.isDeliverySame(localLtc, remoteLtc);
                        });

                        if (!localLtc) {
                            synchronizationNeeded = true;
                        }
                    });

                    localDeliveries.forEach(localLtc => {
                        let remoteLtc = remoteDeliveries.find(remoteLtc => {
                            return DeliveryService.isDeliverySame(localLtc, remoteLtc);
                        });

                        if (!remoteLtc) {
                            synchronisationPromises.push(this.deliveryDbDao.delete(localLtc.id));
                        }
                    });

                    if (synchronizationNeeded) {
                        synchronisationPromises.push(this.deliveryWsDao.getList(this.serversConnectionsProvider.getServerConnection(), startDate, endDate, true)
                            .then(async deliveries => {
                                for (const delivery of deliveries) {
                                    await this.deliveryDbDao.save(delivery.toModel());
                                }
                            }));
                    }

                    Promise.all(synchronisationPromises)
                        .then(async (ignored) => {
                            await this.appSqlProvider.commitBulk();

                            this.getOfflineListByDate(date, true, true, deliveryPickerIdFilter)
                                .then(deliveries => {
                                    this.publishSynchronisationFinished({
                                        day: date,
                                        orders: deliveries,
                                    });

                                });

                            resolve(remoteDeliveries);
                        });

                })
                .catch(reason => {
                        reject(reason);
                    },
                );
        });
    }
}
